import { JsEmail, TemplateVariable } from '../_interfaces/email.interface';

/**
 * Lightweight SHA-256 hashing utility for email template comparison
 * Uses Web Crypto API (built into all modern browsers)
 */
export class EmailHashUtil {

  /**
   * Generate SHA-256 hash of email template content
   * Includes: htmlPart, subjectPart, templateVariables, description
   *
   * This creates a deterministic fingerprint of the template content
   * that can be compared to detect changes.
   */
  static async generateContentHash(email: Partial<JsEmail>): Promise<string> {
    // Normalize the content structure for consistent hashing
    const normalizedContent = {
      htmlPart: email.htmlPart || {},
      subjectPart: email.subjectPart || {},
      description: email.description || '',
      templateVariables: (email.templateVariables || [])
        .map(v => ({
          name: v.name,
          value: v.value
        }))
        .sort((a, b) => a.name.localeCompare(b.name)) // Sort for consistency
    };

    const contentString = JSON.stringify(normalizedContent);
    const encoder = new TextEncoder();
    const data = encoder.encode(contentString);

    // Use Web Crypto API for SHA-256
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  }

  /**
   * Check if draft has changes compared to published version
   */
  static hasUnpublishedChanges(
    currentHash: string,
    publishedSnapshot: { contentHash: string } | null
  ): boolean {
    if (!publishedSnapshot) {
      // Never published = always has changes (unless empty)
      return true;
    }
    return currentHash !== publishedSnapshot.contentHash;
  }

  /**
   * Hash an English source (htmlPart + subjectPart) into a stable hex string.
   * MUST match the backend hashEnSource() in fb-functions/src/index.ts so the
   * frontend can compare locally-computed currentEnHash against the backend-
   * written sourceEnHash to detect translation staleness.
   *
   * Algorithm: SHA-256 of `${html} ${subject}` hex-encoded.
   */
  static async hashEnSource(html: string, subject: string): Promise<string> {
    const input = `${html || ''} ${subject || ''}`;
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Generate hash synchronously using a simple string hash
   * Useful for quick comparisons where crypto API overhead is not needed
   * Note: Less collision-resistant than SHA-256 but faster
   */
  static generateQuickHash(content: string): string {
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }
}
